import { inject, Injectable } from '@angular/core';
import { LoggableAPI } from '../models/protocols/loggable-api';
import { ApiClient } from './api-client';
import { Observable, throwError } from 'rxjs';
import { Remittance } from '../models/remittances/remittance';
import { Endpoints } from './endpoints';
import { CreateRemittanceRequest } from '../models/remittances/create-remittance-request';
import { catchError, map } from 'rxjs/operators';
import { CustomError } from '../models/shared/custom-error';
import { ODataQueryOptions } from '../models/shared/odata-query-options';
import { ODataResponse } from '../models/protocols/odata-response';
import { ContributionEntry } from '../models/remittances/contribution-entry';
import { Contribution } from '../models/remittances/contribution';
import { AddContributionRequest } from '../models/users/requests/add-contribution-request';
import { RemittanceMember } from '../models/remittances/remittance-member';
import { RemittanceHistory } from '../models/remittances/remittance-history';
import { RemittanceAddDatesRequest } from '../models/remittances/remittance-add-dates-request';
import {
  RemittancePotentialMatchedMember,
  RemittanceVerificationMember
} from '../models/remittances/remittance-verification-member';
import { VerifyContributionRequest } from '../models/remittances/verify-contribution-request';
import { ContributionSummary } from '../models/remittances/contribution-summary';
import { RemittanceNewMember } from '../models/remittances/remittance-new-member';
import { RemittanceCreateNewMemberRequest } from '../models/remittances/remittance-create-new-member-request';
import { RemittanceUpdateNewMemberRequest } from '../models/remittances/remittance-update-new-member-request';
import { UpdateContributionRequest } from '../models/remittances/update-contribution-request';
import { RemittanceOwner } from '../models/remittances/remittance-owner';
import { RemittanceComment } from '../models/remittances/remittance-comment';
import { RemittanceCommentRequest } from '../models/remittances/remittance-comment-request';
import { MemberOpenRemittancesByEmployer } from '../models/remittances/member-open-remittances-by-employer';
import { CreateRemittancePaymentRequest } from '../models/payments/create-remittance-payment-request';
import { RemittancePayment } from '../models/payments/remittance-payment';
import { RemittanceOwnerUpdateRequest } from '../models/users/requests/remittance-owner-update-request';
import { FailRemittanceRequest } from '../models/users/requests/fail-remittance-request';
import { HttpResponse } from '@angular/common/http';
import { InternalUserCommentReadReceiptRequest } from '../models/users/requests/internal-user-comment-read-receipt-request';
import { OdataUtils } from '../utils/odata-utils';

@Injectable({
  providedIn: 'root'
})
export class RemittancesAPI implements LoggableAPI {
  private apiClient = inject(ApiClient);

  public serviceName = 'RemittancesAPI';

  public getAllRemittances(odataQueryOptions: ODataQueryOptions): Observable<ODataResponse<Remittance>> {
    const url = Endpoints.getAllRemittances();
    odataQueryOptions.setExpand('Type, Status, PaymentStatus, Source, Owner($expand=ProfileBadgeConfiguration)');
    return this.apiClient.getOdata(url, Remittance, odataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getInternalClosedRemittancesForEmployer(
    employerId: number,
    odataParams: ODataQueryOptions
  ): Observable<ODataResponse<Remittance>> {
    const url = Endpoints.getInternalClosedRemittancesForEmployer(employerId);
    odataParams.setExpand('Type, Status, PaymentStatus, Source, Owner');
    return this.apiClient.getOdata(url, Remittance, odataParams).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getEmployerClosedRemittancesForEmployer(
    odataParams: ODataQueryOptions
  ): Observable<ODataResponse<Remittance>> {
    const url = Endpoints.getEmployerClosedRemittancesForEmployer();
    odataParams.setExpand('Type, Status, PaymentStatus, Source');
    return this.apiClient.getOdata(url, Remittance, odataParams).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetOpenRemittancesForEmployer(employerId: number): Observable<ODataResponse<Remittance>> {
    const url = Endpoints.ipGetOpenRemittancesForEmployer(employerId);
    const oDataQueryOptions = new ODataQueryOptions();
    oDataQueryOptions.setExpand('Type, Status, PaymentStatus, Source, Owner, Transactions($expand=Status)');
    oDataQueryOptions.setOrderBy('Id desc');

    return this.apiClient.getOdata(url, Remittance, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getEmployerOpenRemittancesForEmployer(): Observable<ODataResponse<Remittance>> {
    const url = Endpoints.getEmployerOpenRemittancesForEmployer();
    const oDataQueryOptions = new ODataQueryOptions();
    oDataQueryOptions.setExpand('Type, Status, PaymentStatus, Source');
    oDataQueryOptions.setOrderBy('Id desc');

    return this.apiClient.getOdata(url, Remittance, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getRemittanceById(remittanceId: number, queryOptions: ODataQueryOptions): Observable<Remittance> {
    const url = Endpoints.getRemittanceById(remittanceId);
    return this.apiClient.getOdataObj(url, Remittance, queryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public createRemittance(req: CreateRemittanceRequest): Observable<Remittance> {
    const url = Endpoints.createRemittance();
    return this.apiClient.postObj(Remittance, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public submitRemittance(remittanceId: number): Observable<Remittance> {
    const url = Endpoints.submitRemittance(remittanceId);
    return this.apiClient.putObj(Remittance, url, {} as Remittance).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public updateRemittanceOwner(req: RemittanceOwnerUpdateRequest): Observable<Remittance> {
    const url = Endpoints.updateRemittanceOwner(req.id);
    return this.apiClient.putObj(Remittance, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public updateRemittanceStatusInReview(remittanceId: number): Observable<Remittance> {
    const url = Endpoints.updateRemittanceStatusInReview(remittanceId);
    return this.apiClient.putObj(Remittance, url, null).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public updateRemittanceStatusApproved(remittanceId: number): Observable<Remittance> {
    const url = Endpoints.updateRemittanceStatusApproved(remittanceId);
    return this.apiClient.putObj(Remittance, url, null).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public updateRemittanceStatusFailed(remittanceId: number, req: FailRemittanceRequest): Observable<Remittance> {
    const url = Endpoints.updateRemittanceStatusFailed(remittanceId);
    return this.apiClient.putObj(Remittance, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public addDatesToRemittance(remittanceId: number, req: RemittanceAddDatesRequest): Observable<Remittance> {
    const url = Endpoints.addDatesToRemittance(remittanceId);
    return this.apiClient.putObj(Remittance, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public deleteRemittance(remittanceId: number): Observable<any> {
    const url = Endpoints.deleteRemittance(remittanceId);
    return this.apiClient.deleteWithUntypedRes(url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getMembersOnRemittance(
    employerId: number,
    remittanceId: number,
    searchString?: string
  ): Observable<ODataResponse<RemittanceMember>> {
    const url = Endpoints.getMembersOnRemittance(employerId, remittanceId);
    const queryOptions = new ODataQueryOptions();
    if (searchString) {
      queryOptions.setFilter(`contains(FullName, '${OdataUtils.escapeStringLiteral(searchString)}')`);
    }

    return this.apiClient.getOdata(url, RemittanceMember, queryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getNewMembersOnRemittance(remittanceId: number): Observable<ODataResponse<RemittanceNewMember>> {
    const url = Endpoints.getNewMembersOnRemittance(remittanceId);
    const queryOptions = new ODataQueryOptions();
    queryOptions.setCount(true);
    queryOptions.setExpand('NewMemberDetails($expand=EmploymentType, ProvinceOfEmployment)');
    return this.apiClient.getOdata(url, RemittanceNewMember, queryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getRemittanceOwners(odataQueryOptions: ODataQueryOptions): Observable<ODataResponse<RemittanceOwner>> {
    const url = Endpoints.getRemittanceOwners();
    return this.apiClient.getOdata(url, RemittanceOwner, odataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getContributionsForRemittance(
    remittanceId: number,
    queryOptions: ODataQueryOptions
  ): Observable<ODataResponse<Contribution>> {
    const url = Endpoints.getContributionsForRemittance(remittanceId);
    queryOptions.setExpand('NewMemberDetails, Entries($expand=Code;$count=true)');
    return this.apiClient.getOdata(url, Contribution, queryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public addContribution(remittanceId: number, req: AddContributionRequest): Observable<Contribution> {
    const url = Endpoints.addContributionToRemittance(remittanceId);
    return this.apiClient.postObj(Contribution, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public updateContribution(
    remittanceId: number,
    contributionId: string,
    req: UpdateContributionRequest
  ): Observable<Contribution> {
    const url = Endpoints.updateContribution(remittanceId, contributionId);
    return this.apiClient.putObj(Contribution, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public deleteContribution(remittanceId: number, contributionId: string): Observable<any> {
    const url = Endpoints.deleteContribution(remittanceId, contributionId);
    return this.apiClient.deleteWithUntypedRes(url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public createContributionEntry(
    remittanceId: number,
    contributionId: string,
    entries: ContributionEntry[]
  ): Observable<Contribution> {
    const url = Endpoints.createContributionEntry(remittanceId, contributionId);
    return this.apiClient.postObj(Contribution, url, entries).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public updateContributionEntry(
    remittanceId: number,
    contributionId: string,
    entry: any
  ): Observable<ContributionEntry> {
    const url = Endpoints.updateContributionEntry(remittanceId, contributionId, entry.id);
    return this.apiClient.putObj(ContributionEntry, url, entry).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public deleteContributionEntry(remittanceId: number, contributionId: string, entryId: string): Observable<any> {
    const url = Endpoints.deleteContributionEntry(remittanceId, contributionId, entryId);
    return this.apiClient.deleteWithUntypedRes(url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public createRemittanceNewMember(
    remittanceId: number,
    req: RemittanceCreateNewMemberRequest
  ): Observable<RemittanceNewMember> {
    const url = Endpoints.createRemittanceNewMember(remittanceId, req.contributionId);
    return this.apiClient.postObj(RemittanceNewMember, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public updateRemittanceNewMember(
    remittanceId: number,
    req: RemittanceUpdateNewMemberRequest
  ): Observable<RemittanceNewMember> {
    const url = Endpoints.updateRemittanceNewMember(remittanceId, req.contributionId, req.id);
    return this.apiClient.putObj(RemittanceNewMember, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getRemittanceHistory(remittanceId: number): Observable<RemittanceHistory[]> {
    const url = Endpoints.getRemittanceHistory(remittanceId);
    return this.apiClient.getOdata(url, RemittanceHistory).pipe(
      map(res => res.value),
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getRemittanceComments(remittanceId: number): Observable<RemittanceComment[]> {
    const url = Endpoints.getCommentsForRemittance(remittanceId);
    const odataOptions = new ODataQueryOptions();
    odataOptions.setExpand('Mentions');
    return this.apiClient.getOdata(url, RemittanceComment, odataOptions).pipe(
      map(res => {
        const comments = res.value;
        return comments.sort((a, b) => {
          return new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime();
        });
      }),
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberOpenRemittancesByEmployer(memberId: number): Observable<MemberOpenRemittancesByEmployer[]> {
    const url = Endpoints.ipGetMemberOpenRemittancesByEmployer(memberId);
    return this.apiClient.getArr(MemberOpenRemittancesByEmployer, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public addRemittanceComment(req: RemittanceCommentRequest): Observable<RemittanceComment[]> {
    const url = Endpoints.addRemittanceComment(req.remittanceId);
    return this.apiClient.simplePost(url, req).pipe(
      map(res => {
        const comments = window?.injector?.Deserialize?.arrayOf(RemittanceComment, res.body);
        return comments.sort((a, b) => {
          return new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime();
        });
      }),
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public setRemittanceCommentsAsRead(req: InternalUserCommentReadReceiptRequest): Observable<HttpResponse<any>> {
    const url = Endpoints.setRemittanceCommentsAsRead();
    return this.apiClient.simplePost(url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getContributionHistory(remittanceId: string, contributionId: string): Observable<RemittanceHistory[]> {
    const url = Endpoints.getHistoryForContribution(remittanceId, contributionId);
    return this.apiClient.getOdata(url, RemittanceHistory).pipe(
      map(res => res.value),
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getRemittanceMembersToVerify(remittanceId: string): Observable<ODataResponse<RemittanceVerificationMember>> {
    const odataOptions = new ODataQueryOptions();
    odataOptions.setExpand('PotentialMatches');
    odataOptions.setCount(true);
    const url = Endpoints.getRemittanceMembersToVerify(remittanceId);
    return this.apiClient.getOdata(url, RemittanceVerificationMember, odataOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public verifyContribution(
    remittanceId: string,
    contributionId: string,
    req: VerifyContributionRequest
  ): Observable<Contribution> {
    const url = Endpoints.verifyContribution(remittanceId, contributionId);
    return this.apiClient.putObj(Contribution, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getContributionSummaryForRemittance(remittanceId: string): Observable<ContributionSummary> {
    const url = Endpoints.getContributionSummaryForRemittance(remittanceId);
    return this.apiClient.getOdataObj(url, ContributionSummary).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public createRemittancePayment(req: CreateRemittancePaymentRequest): Observable<RemittancePayment> {
    const url = Endpoints.createRemittancePayment();
    return this.apiClient.postObj(RemittancePayment, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getPotentialMatchesForContribution(
    remittanceId: string,
    contributionId: string
  ): Observable<ODataResponse<RemittancePotentialMatchedMember>> {
    const url = Endpoints.getPotentialMatchesForContribution(remittanceId, contributionId);
    return this.apiClient.getOdata(url, RemittancePotentialMatchedMember).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }
}
